import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useEffect } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { ConfigProvider } from 'antd';
import theme from './theme';
import { SUPPORTED_LOCALES, i18n } from './i18n';
import { ProfileContextProvider } from './contexts/profile.context';
import { ProtectedRoute } from './components/protected-routes/protected-routes.component';
import LoginPage from './Pages/login/login.page';
import RegisterPage from './Pages/register/register.page';
import ForgotPassword from './Pages/password/forgot-password.page';
import ResetPassword from './Pages/password/reset-password.page';
import EmailConfirm from './Pages/email-verify/email-verify.page';
import { Settings } from './Pages/settings/settings.page';
import { ProfileSection, UpdatePasswordSection, UserManagement } from './components/pages/settings';
import { AdminProtectedRoute } from './components/admin-protected-route/admin-protected-route.component';
import { LoggedOutRoute } from './components/shared/logged-out-route/logged-out-route.component';
import LanguageSwitcher from './components/language-switcher/language-switcher.component';

const reCaptchaSitekey = process.env.REACT_APP_PUBLIC_RECAPTCHA_SITE_KEY || '';

const LocaleLayout = () => {
  const { locale } = useParams<{ locale: string }>();
  const location = useLocation();
  const isSupportedLocale = SUPPORTED_LOCALES.includes(locale as typeof SUPPORTED_LOCALES[number]);

  useEffect(() => {
    if (!isSupportedLocale) return;

    void i18n.changeLanguage(locale);
    document.documentElement.lang = locale as string;
    document.documentElement.dir = locale === 'ar' || locale === 'he' ? 'rtl' : 'ltr';
  }, [isSupportedLocale, locale]);

  if (!isSupportedLocale) {
    return <Navigate to={`/en${location.pathname}${location.search}${location.hash}`} replace />;
  }

  return (
    <>
      <LanguageSwitcher />
      <Outlet />
    </>
  );
};

function App() {
  return (
    <ProfileContextProvider>
      <BrowserRouter basename="/">
        <ConfigProvider theme={theme}>
          <div className="App">
            <GoogleReCaptchaProvider reCaptchaKey={reCaptchaSitekey}>
              <Routes>
                <Route path="/" element={<Navigate to="/en/login" replace />} />
                <Route path="/:locale" element={<LocaleLayout />}>
                  <Route element={<LoggedOutRoute />}>
                    <Route path="login" element={<LoginPage />} />
                    <Route path="register" element={<RegisterPage />} />
                    <Route path="forgot-password" element={<ForgotPassword />} />
                    <Route path="reset-password-by-token" element={<ResetPassword />} />
                    <Route path="verify-email" element={<EmailConfirm />} />
                    <Route path="*" element={<Navigate to="login" replace />} />
                  </Route>
                  <Route element={<ProtectedRoute />}>
                    <Route path="settings/:section?" element={<Settings />}>
                      <Route path="profile" element={<ProfileSection />} />
                      <Route path="update-password" element={<UpdatePasswordSection />} />
                      <Route element={<AdminProtectedRoute />}>
                        <Route path="user-management" element={<UserManagement />} />
                      </Route>
                    </Route>
                  </Route>
                </Route>
              </Routes>
            </GoogleReCaptchaProvider>
          </div>
        </ConfigProvider>
      </BrowserRouter>
    </ProfileContextProvider>
  );
}

export default App;
